import { CloseOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Empty, Input, Modal, message } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import LocalImage from '@/components/Common/LocalImage';
import { fetchInsFamousPostCommentsData, sendInsFamousRequireAddRequest } from '@/lib/service/anonymously';
import { cn } from '@/lib/utils';
import { InstagramPostsCommentsItemModel, InstagramQueryPostItemModel } from '@/types/anonymously.interface';
import './modal-override.less';
import useRegisterOrLoginGuide from '@/lib/hooks/common/useRegisterOrLoginGuide';
import PostCommentsSkeleton from './PostCommentsSkeleton';

interface PostCommentInfoProps {
  item: InstagramQueryPostItemModel;
  /** 是否为移动端预览 */
  isMobilePreview?: boolean;
  /** 控制显示的数量上限 */
  maxDisplayCount?: number | false;
  /** 自定义加载按钮点击事件，将取代默认加载事件 */
  onLoadMoreClick?: () => void;
}

const PostComments = ({ item, isMobilePreview, maxDisplayCount, onLoadMoreClick }: PostCommentInfoProps) => {
  const { t } = useTranslation();
  const { guideToRegisterOrLogin } = useRegisterOrLoginGuide();

  // 评论数据
  const [commentsData, setCommentsData] = useState<InstagramPostsCommentsItemModel[]>([]);
  const [firstPageCommentsData, setFirstPageCommentsData] = useState<InstagramPostsCommentsItemModel[]>([]);
  // 评论总数
  const [totalComments, setTotalComments] = useState<number>(0);
  // 分页token
  const [paginationToken, setPaginationToken] = useState<string>('');
  // 加载状态
  const [loading, setLoading] = useState<boolean>(false);
  // 初始加载状态
  const [initialLoading, setInitialLoading] = useState<boolean>(false);
  // 搜索关键词
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  // 是否有更多数据
  const [hasMore, setHasMore] = useState<boolean>(true);
  // 错误信息
  const [error, setError] = useState<string>('');
  // 回复对话框可见性
  const [replyModalVisible, setReplyModalVisible] = useState<boolean>(false);
  const [replayModalButtonLoading, setReplayModalButtonLoading] = useState<boolean>(false);
  // 请求状态引用
  const fetchingRef = useRef<{ postCode: string | null; isFetching: boolean }>({
    postCode: null,
    isFetching: false,
  });

  // 获取评论数据
  const fetchComments = useCallback(
    async (code: string, token?: string) => {
      if (!code) return;

      // 如果正在获取相同帖子的评论，则跳过请求
      if (fetchingRef.current.isFetching && fetchingRef.current.postCode === code && !token) {
        return;
      }

      fetchingRef.current = {
        postCode: code,
        isFetching: true,
      };

      // 如果是初始加载（没有token），设置initialLoading为true
      if (!token) {
        setInitialLoading(true);
      } else {
        // 如果第一页数据展示完，显示剩余的评论数据
        if (commentsData.length < firstPageCommentsData.length) {
          setCommentsData(firstPageCommentsData);
          fetchingRef.current.isFetching = false;
          return;
        }
        setLoading(true);
      }

      setError('');

      try {
        const res = await fetchInsFamousPostCommentsData(code, token);
        if (res?.code === 0 && res?.data) {
          const { comments = [], comment_count = 0, pagination_token } = res.data;

          if (token) {
            // 加载更多
            setCommentsData((prev) => [...prev, ...comments]);
          } else {
            // 初始加载
            if (maxDisplayCount === false) {
              setCommentsData(comments);
              setHasMore(!!pagination_token);
            } else {
              setCommentsData(comments.slice(0, maxDisplayCount as number));
              setHasMore(comments.length > (maxDisplayCount as number));
            }
            setFirstPageCommentsData(comments);
          }

          setTotalComments(comment_count);
          setPaginationToken(pagination_token || '');
        } else {
          setError(res?.message || t('Failed to load comments'));
          console.error('Failed to fetch comments:', res?.message);
        }
      } catch (error) {
        setError(t('Failed to load comments'));
        console.error('Error fetching comments:', error);
      } finally {
        if (!token) {
          setInitialLoading(false);
        } else {
          setLoading(false);
        }
        fetchingRef.current.isFetching = false;
      }
    },
    [t, maxDisplayCount]
  );

  // 监听 item.code 变化重新获取数据
  useEffect(() => {
    if (item?.code) {
      // 重置状态
      setCommentsData([]);
      setPaginationToken('');
      setHasMore(true);
      setSearchKeyword('');
      setError('');
      setTotalComments(0); // 重置评论总数

      // 只有在没有获取过当前帖子的评论时才获取数据
      if (fetchingRef.current.postCode !== item.code) {
        fetchComments(item.code);
      }
    }
  }, [item?.code, fetchComments]);

  // 加载更多评论
  const handleLoadMore = () => {
    guideToRegisterOrLogin(() => {
      if (onLoadMoreClick) {
        onLoadMoreClick();
      } else {
        if (loading || !hasMore || !item?.code) return;
        fetchComments(item.code, paginationToken);
      }
    });
  };

  // 搜索框变化
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchKeyword(e.target.value);
  };

  // 清除搜索
  const clearSearch = () => {
    setSearchKeyword('');
  };

  // 重试加载
  const handleRetry = () => {
    if (!item?.code) return;
    fetchComments(item.code);
  };

  // 打开回复对话框
  const handleReplyClick = () => {
    guideToRegisterOrLogin(() => {
      setReplyModalVisible(true);
    });
  };

  // 关闭回复对话框
  const handleCloseModal = () => {
    setReplyModalVisible(false);
  };

  // 处理"I'm Interested"按钮点击
  const handleInterestedClick = async () => {
    // 发送请求记录用户对这个功能感兴趣
    try {
      setReplayModalButtonLoading(true);
      const res = await sendInsFamousRequireAddRequest();
      if (res?.code === 0) {
        // 关闭对话框
        setReplyModalVisible(false);

        // 显示成功消息
        message.success(t("Thank you for your feedback! We've recorded your interest."));
      } else {
        message.error(res.message);
      }
    } catch (error) {
      message.error(t('Failed to record your feedback'));
    } finally {
      setReplayModalButtonLoading(false);
    }
  };

  // 过滤后的评论数据
  const filteredComments = useMemo(() => {
    if (!searchKeyword.trim()) return commentsData;

    const keyword = searchKeyword.trim().toLowerCase();
    return commentsData.filter((comment) => {
      const username = comment.media_user_dto?.username?.toLowerCase() || '';
      const fullName = comment.media_user_dto?.full_name?.toLowerCase() || '';
      const text = comment.text?.toLowerCase() || '';

      return username.includes(keyword) || fullName.includes(keyword) || text.includes(keyword);
    });
  }, [commentsData, searchKeyword]);

  // 格式化日期时间，使用dayjs
  const formatDateTime = (dateTimeStr?: string) => {
    if (!dateTimeStr) return '';

    try {
      return dayjs(dateTimeStr).format('YYYY-MM-DD HH:mm:ss');
    } catch (error) {
      return dateTimeStr;
    }
  };

  // 在新标签页打开当前页面，携带 media_name 参数
  const handleOpenInNewTab = (mediaName: string) => {
    guideToRegisterOrLogin(() => {
      window.open(`/instagram-viewer/post-comments?media_name=${mediaName}`, '_blank');
    });
  };

  // 渲染评论列表内容
  const renderCommentListContent = () => {
    // 显示错误
    if (error) {
      return (
        <div className="flex flex-col items-center justify-center h-32 text-gray-500 p-4">
          <p className="text-red-500 mb-2">{error}</p>
          <Button onClick={handleRetry}>{t('Retry')}</Button>
        </div>
      );
    }

    // 显示初始加载状态 - 使用骨架屏保持布局稳定
    if (initialLoading) {
      return <PostCommentsSkeleton isMobilePreview={isMobilePreview} />;
    }

    // 显示无数据状态
    if (filteredComments.length === 0 && !loading) {
      return (
        <div className="flex flex-col items-center justify-center h-60 text-gray-500 p-6">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={searchKeyword ? t('No matching comments found') : t('No comments yet')}
          />
          {searchKeyword && (
            <Button type="link" onClick={clearSearch} className="mt-2">
              {t('Clear search')}
            </Button>
          )}
        </div>
      );
    }

    // 显示评论列表
    return (
      <>
        <div>
          {filteredComments.map((comment, index) => (
            <div key={index} className="p-4 hover:bg-gray-50 transition-colors max-md:px-0 max-md:py-2.5">
              <div className="flex items-start gap-3">
                {/* 用户头像 */}
                <div
                  className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 cursor-pointer"
                  onClick={() => handleOpenInNewTab(comment.media_user_dto?.username || '')}
                >
                  <LocalImage
                    src={comment.media_user_dto?.profile_pic_url}
                    width={40}
                    height={40}
                    alt={comment.media_user_dto?.full_name || ''}
                  />
                </div>

                <div className="flex-1 min-w-0">
                  {/* 用户信息和评论时间 */}
                  <div className="flex flex-wrap items-center mb-1.5">
                    <span
                      className="font-medium mr-1 cursor-pointer"
                      onClick={() => handleOpenInNewTab(comment.media_user_dto?.username || '')}
                    >
                      {comment.media_user_dto?.full_name || ''}
                    </span>

                    <a
                      className="flex items-center text-gray-500 text-sm"
                      href={`https://www.instagram.com/${comment.media_user_dto?.username}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {/* Instagram图标 */}
                      <Image
                        src="/images/follow/ins-mark.webp"
                        width={16}
                        height={16}
                        alt="Instagram"
                        className="mx-1"
                      />

                      <span>@{comment.media_user_dto?.username}</span>

                      {/* 蓝V标识 */}
                      {comment.media_user_dto?.is_verified && (
                        <Image
                          src="/images/follow/blue-mark.webp"
                          width={16}
                          height={16}
                          alt="Verified"
                          className="ml-1"
                        />
                      )}
                    </a>

                    <span className="ml-auto text-xs text-[#B6B6B6]">{formatDateTime(comment.comment_time)}</span>
                  </div>

                  {/* 评论内容 */}
                  <p className="text-base leading-5 whitespace-pre-line break-words">{comment.text}</p>

                  {/* 点赞和回复数 */}
                  <div className="h-5 flex items-center mt-2 text-sm text-[#6D6D6D] max-md:mt-1.5">
                    {comment.comment_like_count > 0 && (
                      <span className="mr-4">
                        {comment.comment_like_count} {t('Likes')}
                      </span>
                    )}

                    {comment.child_comment_count > 0 && (
                      <span className="cursor-pointer hover:text-blue-500" onClick={handleReplyClick}>
                        {comment.child_comment_count} {t('Reply')}
                      </span>
                    )}

                    {/* 当没有回复时，也显示回复按钮，便于用户点击 */}
                    {/* {!comment.child_comment_count && (
                      <span
                        className="cursor-pointer hover:text-blue-500"
                        onClick={handleReplyClick}
                      >
                        {t('Reply')}
                      </span>
                    )} */}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 加载更多按钮和加载状态合二为一 */}
        {hasMore && filteredComments.length > 0 && (
          <div className="p-4 text-center max-md:px-0 max-md:py-2.5">
            <Button
              onClick={handleLoadMore}
              loading={loading}
              className="px-6 font-medium max-md:w-full max-md:h-[42px] max-md:p-0 max-md:text-sm"
            >
              {loading ? t('Loading...') : t('Load More')}
            </Button>
          </div>
        )}
      </>
    );
  };

  return (
    <div
      className={cn(
        'w-full flex flex-col border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm h-full',
        'max-md:border-none max-md:shadow-none'
      )}
    >
      {/* 标题和搜索框 - 固定部分 */}
      <div className="p-4 bg-white border-gray-200 max-md:p-0 max-md:pb-2.5">
        <div className="flex flex-col gap-2">
          <h3 className="text-lg font-medium max-md:text-sm">
            {totalComments} {t('Comments')}
          </h3>
          {!isMobilePreview && (
            <div className="relative">
              <Input
                placeholder={t('Search by username or comment content')}
                prefix={<SearchOutlined className="text-gray-400" />}
                value={searchKeyword}
                onChange={handleSearchChange}
                className="w-full h-9 rounded-lg max-md:h-10"
                allowClear
              />
            </div>
          )}
        </div>
      </div>

      {/* 评论列表 - 可滚动部分 */}
      <div className="flex-1 overflow-y-auto bg-white" style={{ maxHeight: 'calc(100% - 120px)' }}>
        {renderCommentListContent()}
      </div>

      {/* 回复功能询问对话框 */}
      <Modal
        open={replyModalVisible}
        onCancel={handleCloseModal}
        footer={null}
        closable={false}
        width={400}
        centered
        title={null}
        className="reply-feature-modal"
        bodyStyle={{ padding: '24px' }}
        style={{ borderRadius: '16px', overflow: 'hidden' }}
      >
        <div>
          <div className="text-xl font-medium text-center relative">
            {t('View Comment Replies?')}
            <CloseOutlined
              className="absolute right-0 top-1 cursor-pointer"
              style={{ fontSize: '16px', color: '#666' }}
              onClick={handleCloseModal}
            />
          </div>
          <p className="text-base text-center leading-relaxed">
            {t(
              'Would you like to see replies within comment threads? If this feature is important to you, please let us know and we will consider adding it in future updates.'
            )}
          </p>

          <div className="flex justify-between gap-4">
            <Button className="not-now-btn flex-1 text-base font-medium" onClick={handleCloseModal}>
              {t('Not Now')}
            </Button>

            <Button
              className="interested-btn flex-1"
              color="primary"
              loading={replayModalButtonLoading}
              onClick={handleInterestedClick}
            >
              {t("I'm Interested")}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

// 使用 memo 包装组件避免不必要的重渲染
export default memo(PostComments, (prevProps, nextProps) => {
  // 只有当 item.code 或 isMobilePreview 改变时才重新渲染
  return (
    prevProps.item?.code === nextProps.item?.code &&
    prevProps.isMobilePreview === nextProps.isMobilePreview &&
    prevProps.maxDisplayCount === nextProps.maxDisplayCount
  );
});
